import { colord } from 'colord';
import { generatePalette } from './generatePalette';
import { WhiteLabelConfig, WhiteLabelThemes, Theme } from './types';
import { COLOR_PALETTE, COLORS } from './colors';

/**
 * Helper function to determine the best contrasting text color
 */
function getBestContrastColor(backgroundColor: string): string {
  const bgColor = colord(backgroundColor);
  const isLight = bgColor.isLight();

  // If background is light, use dark text; if dark, use light text
  return isLight ? '#000000' : '#ffffff';
}

/**
 * Creates dynamic themes based on white label configuration
 * Generates color palettes
 */
export function createDynamicThemes(
  config: WhiteLabelConfig
): WhiteLabelThemes {
  const primaryPalette = generatePalette(config.primaryColor);
  const secondaryPalette = generatePalette(config.secondaryColor);

  const dynamicLightTheme: Theme = {
    faviconUrl: config.faviconUrl,
    logoUrl: config.logoUrl,
    mode: 'light',
    colors: {
      ...COLORS,
    },
    semantic: {
      primary: primaryPalette.p50,
      primaryHover: primaryPalette.p60,
      onPrimary: getBestContrastColor(primaryPalette.p150),
      primaryContainer: primaryPalette.p10,

      secondary: secondaryPalette.p50,
      secondaryHover: secondaryPalette.p0,
      onSecondary: getBestContrastColor(secondaryPalette.p0),

      background: '#F5F7F9',
      onBackground: '#191C1E',
      surface: '#ffffff',
      onSurface: '#191C1E',
      border: '#dfe1e3',

      neutral: '#ffffff',
      onNeutral: '#000000',

      error: COLOR_PALETTE.red.p70,
      onError: COLORS.white,
      errorLight: '#f23218',
      errorContainer: '#ffe5e8',
      success: COLOR_PALETTE.green.p70,
      onSuccess: COLORS.white,
      warning: COLOR_PALETTE.yellow.p50,
      onWarning: COLORS.black,
      info: COLOR_PALETTE.blue.p50,
      infoLight: '#216FD9',
      onInfo: COLORS.white,
    },
  };

  const dynamicDarkTheme: Theme = {
    faviconUrl: config.faviconUrl,
    logoUrl: config.logoUrl,
    mode: 'dark',
    colors: {
      ...COLORS,
    },
    semantic: {
      primary: primaryPalette.p50,
      primaryHover: primaryPalette.p70,
      onPrimary: getBestContrastColor(primaryPalette.p50),
      primaryContainer: primaryPalette.p150,

      secondary: secondaryPalette.p50,
      secondaryHover: secondaryPalette.p10,
      onSecondary: getBestContrastColor(secondaryPalette.p10),

      background: '#191C1E',
      onBackground: '#F5F7F9',
      surface: '#000000',
      onSurface: '#ffffff',
      border: '#444749',

      neutral: '#ffffff',
      onNeutral: '#000000',

      error: COLOR_PALETTE.red.p70,
      onError: COLORS.white,
      errorLight: '#f23218',
      errorContainer: '#ffe5e8',
      success: COLOR_PALETTE.green.p70,
      onSuccess: COLORS.white,
      warning: COLOR_PALETTE.yellow.p70,
      onWarning: COLORS.black,
      info: COLOR_PALETTE.blue.p70,
      infoLight: '#216FD9',
      onInfo: COLORS.white,
    },
  };

  return {
    light: dynamicLightTheme,
    dark: dynamicDarkTheme,
  };
}

/**
 * Applies CSS custom properties for the current theme to the document
 */
export function applyCssCustomProperties(
  config: WhiteLabelConfig,
  theme: Theme
) {
  const root = document.documentElement;

  // Generate primary palette for CSS custom properties
  const primaryPalette = generatePalette(config.primaryColor);

  // Apply primary palette variants as CSS custom properties
  Object.entries(primaryPalette).forEach(([key, value]) => {
    root.style.setProperty(`--wl-primary-${key}`, value);
  });

  root.style.setProperty('--wl-primary-container', '#FFE6DF');

  // Apply basic colors as CSS custom properties
  root.style.setProperty('--wl-primary-color', config.primaryColor);
  root.style.setProperty('--wl-secondary-color', theme.semantic.secondary);
  root.style.setProperty('--wl-surface-color', theme.semantic.surface);
  root.style.setProperty('--wl-brand-name', `"${config.brandName}"`);

  // Apply button-specific colors based on theme mode
  root.style.setProperty('--wl-on-primary', theme.semantic.onPrimary);
  root.style.setProperty('--wl-primary-hover', theme.semantic.primaryHover);
  root.style.setProperty('--wl-on-secondary', theme.semantic.onSecondary);
  root.style.setProperty('--wl-secondary-hover', theme.semantic.secondaryHover);
  root.style.setProperty('--wl-on-surface', theme.semantic.onSurface);
  root.style.setProperty('--wl-border', theme.semantic.border);

  root.style.setProperty('--error', theme.semantic.error);
  root.style.setProperty('--on-error', theme.semantic.onError);
  root.style.setProperty('--error-light', theme.semantic.errorLight);
  root.style.setProperty('--error-container', theme.semantic.errorContainer);

  root.style.setProperty('--success', theme.semantic.success);
  root.style.setProperty('--on-success', theme.semantic.onSuccess);

  // Apply semantic colors
  Object.entries(theme.semantic).forEach(([key, value]) => {
    const cssVarName = `--wl-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVarName, value);
  });

  // Apply custom CSS properties if provided
  if (config.customCssProperties) {
    Object.entries(config.customCssProperties).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
  }

  // Apply default white label styling properties if not overridden
  const defaultProperties = {
    '--wl-font-family': 'Inter, sans-serif',
    '--wl-border-radius': '8px',
    '--wl-box-shadow': '0px 4px 8px rgba(0, 0, 0, 0.1)',
    '--wl-background': theme.semantic.background,
    '--wl-background-light': '#F5F7F9',
    '--wl-on-background': theme.semantic.onBackground,
    '--wl-surface': theme.semantic.surface,
    '--wl-on-surface': theme.semantic.onSurface,
  };

  Object.entries(defaultProperties).forEach(([property, value]) => {
    // Only set if not already set by customCssProperties
    if (!config.customCssProperties?.[property]) {
      root.style.setProperty(property, value);
    }
  });
}

/**
 * Gets the current theme mode from Redux or localStorage
 */
export function getCurrentThemeMode(): 'light' | 'dark' {
  // Check if dark class is applied to document (most reliable)
  if (document.documentElement.classList.contains('dark')) {
    return 'dark';
  }

  // Fallback to localStorage check
  try {
    const persistedState = localStorage.getItem('persist:layout');
    if (persistedState) {
      const layoutState = JSON.parse(persistedState);
      const theme = JSON.parse(layoutState.theme || '"LIGHT"');
      return theme === 'DARK' ? 'dark' : 'light';
    }
  } catch (error) {
    console.warn('Could not read theme from localStorage:', error);
  }

  // Final fallback to light mode (instead of system preference)
  return 'light';
}

/**
 * Creates a single theme object based on current mode
 */
export function createCurrentTheme(config: WhiteLabelConfig): Theme {
  const themes = createDynamicThemes(config);
  const currentMode = getCurrentThemeMode();
  return themes[currentMode];
}
